import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';

import { tickerColumns } from '../../service/tickercolumns';

import Currency from '../../containers/currencycalculator/Currency';
import HeaderComponent from '../HeaderComponent';

import css from '../../styles/instrument/currencycalculator';

class CurrencyCalculator extends Component {
    static propTypes = {
        symbol: PropTypes.string,
        instrument: PropTypes.bool,
        defaultValue: PropTypes.number,

        list: PropTypes.array,
        isLoading: PropTypes.bool,
        hasFetched: PropTypes.bool,

        breakpoint: PropTypes.string,

        modalOpen: PropTypes.bool.isRequired,
        openModal: PropTypes.func.isRequired,
        closeModal: PropTypes.func.isRequired,

        requestTickerData: PropTypes.func,
        fetchCryptocurrency: PropTypes.func,
        t: PropTypes.func.isRequired,
    }

    static defaultProps = {
        defaultValue: 100,
    };

    constructor(props) {
        super(props);

        this.state = {
            multiplier: 1,
            inputs: {
                input0: props.defaultValue,
                input1: props.defaultValue,
            },
            currency: {
                input0: {
                    multiplier: 1,
                    itemSector: props.symbol,
                },
                input1: {
                    multiplier: 1,
                    itemSector: 'X:SSEKSEK.IDCFX',
                },
            },
        };

        this._getOtherInput = id => {
            const { inputs } = this.state;

            for (const key in inputs) {
                if (inputs.hasOwnProperty(key) && key !== id) {
                    return key;
                }
            }

            return this.input1.id;
        };

        this._calculate = (id, forcedMultiplier = null) => {
            const state = this.state;
            const value = this.state.inputs[id];

            let multiplier;
            if (!forcedMultiplier) {
                multiplier = this.state.multiplier;
            } else {
                multiplier = forcedMultiplier;
                state.multiplier = forcedMultiplier;
            }

            if (value && !isNaN(value)) {
                const newValue = id === 'input0' ?
                    parseFloat(value) * multiplier :
                    parseFloat(value) / multiplier;

                state.inputs[this._getOtherInput(id)] = newValue.toFixed(2);

                this.setState(state);
            }
        };

        this._updateCalculation = (state, input) => {
            this.setState(state, () => {
                this._calculate(input);
            });
        };

        this.onChange = ({ target: { value, id } }) => {
            const state = this.state;
            state.inputs[id] = value;

            this._updateCalculation(state, id);
        };

        this.onFocus = ({ target: { value, id } }) => {
            const state = this.state;

            if (value % 1 !== 0) {
                state.inputs[id] = Math.round(value).toString();
                this._updateCalculation(state, id);
            }
        };

        this.onBlur = ({ target: { id } }) => {
            const state = this.state;

            if (!state.inputs[id]) {
                state.inputs[id] = this.props.defaultValue;
                this._updateCalculation(state, id);
            }
        };

        this._generateMultiplier = (input = null, id = null) => {
            if (!input) {
                const { input0, input1 } = this.state.currency;
                return input0.multiplier / input1.multiplier;
            }

            const otherId = this._getOtherInput(id);
            if (id === 'input0') {
                return input.multiplier / this.state.currency[otherId].multiplier;
            }

            return this.state.currency[otherId].multiplier / input.multiplier;
        };

        this.changeCurrency = (id, symbol) => {
            if (symbol) {
                const otherId = this._getOtherInput(id);

                // Dont try to fetch data from fake ticker SEKSEK
                if (symbol === 'X:SSEKSEK.IDCFX') {
                    this.setState({
                        currency: {
                            [id]: { itemSector: 'X:SSEKSEK.IDCFX', multiplier: 1 },
                            [otherId]: this.state.currency[otherId],
                        },
                    });

                    return;
                }

                const newState = this.state;
                if (symbol.indexOf('.CRYPTO') > -1) {
                    this.props.fetchCryptocurrency(symbol).then(res => {
                        const { LAST_SEK } = res.payload.values;
                        newState.currency = {
                            [id]: { itemSector: symbol, multiplier: LAST_SEK },
                            [otherId]: this.state.currency[otherId],
                        };
                        this.setState(newState);
                    });
                } else {
                    this.props.requestTickerData(symbol, tickerColumns).then((res) => {
                        const input = this.state.currency[id];
                        const { LAST, LASTNZ, LASTNZ_DIV } = res.payload.values;
                        input.itemSector = symbol;
                        input.multiplier = LAST || LASTNZ || LASTNZ_DIV;
                        newState.currency = {
                            [id]: input,
                            [otherId]: this.state.currency[otherId],
                        };
                        this.setState(newState);
                    });
                }
            }
        };

        this._closeModal = () => {
            this.props.closeModal();
        };

        // Get multiplier from Currency-component and update values
        this.getMultiplier = (ticker, id) => {
            const otherId = this._getOtherInput(id);
            const input = this.state.currency[id];
            input.multiplier = ticker.LAST || ticker.LASTNZ || ticker.LASTNZ_DIV;

            const adjustedMultiplier = this._generateMultiplier(input, id);

            this.setState({
                currency: {
                    [id]: input,
                    [otherId]: this.state.currency[otherId],
                },
            }, () => {
                if (id === 'input0') {
                    this._calculate(id, adjustedMultiplier);
                } else {
                    this._calculate(otherId, adjustedMultiplier);
                }
            });
        };

        this.getMultiplier = this.getMultiplier.bind(this);
        this.changeCurrency = this.changeCurrency.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        this._calculate('input0');
    }

    render() {
        const { symbol, isLoading, instrument, breakpoint, t } = this.props;

        if (!symbol.match(/SEK.IDCFX$/i) && !symbol.match(/CRYPTO$/i)) {
            return null;
        }

        if (isLoading) {
            return <div />;
        }

        return (
            <HeaderComponent title={t('currencyCalculator')}>
                <div className={css.container}>
                    <Currency
                        id="input0"
                        symbol={this.state.currency.input0.itemSector}
                        value={parseFloat(this.state.inputs.input0, 10)}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                        onFocus={this.onFocus}
                        currencyList={this.props.list}
                        changeCurrency={this.changeCurrency}
                        onNewProps={this.getMultiplier}
                        changeable={!instrument}
                        breakpoint={breakpoint === 'mobile-small'}
                    />
                    <Currency
                        id="input1"
                        symbol={this.state.currency.input1.itemSector}
                        value={parseFloat(this.state.inputs.input1, 10)}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                        onFocus={this.onFocus}
                        currencyList={this.props.list}
                        changeCurrency={this.changeCurrency}
                        onNewProps={this.getMultiplier}
                        changeable
                        breakpoint={breakpoint === 'mobile-small'}
                    />
                    <div className={css.icon}>
                        <span>=</span>
                    </div>
                </div>
            </HeaderComponent>
        );
    }
}

export default translate('currency')(CurrencyCalculator);



/** WEBPACK FOOTER **
 ** /src/shared/components/instrument/CurrencyCalculator.js
 **/